import { useEffect, type ReactElement } from 'react'
import confetti from 'canvas-confetti'
import { AppShell } from '../components/AppShell.js'
import { Icon } from '../components/installer/icons.js'
import { useDemoStore } from '../state/store.js'
import { useStep } from '../state/useStep.js'

// On-brand burst colors: Veritact deep blue, signal blue, success green, cream, white.
const CONFETTI_COLORS = ['#1E3F95', '#2A52D6', '#2F855A', '#F4F2EC', '#ffffff']

/**
 * Terminal success screen — the sealed proof bundle. Ported from the Lovable
 * completion overlay (Index.tsx 763-774), with a celebratory confetti burst.
 */
export const JobClosedScreen = (): ReactElement => {
  const { dispatch } = useDemoStore()
  const { goto } = useStep()

  // Fire a celebratory burst on mount. `confetti.reset()` on unmount tears down
  // the canvas the library appends to <body> — this module owns that canvas, so
  // it cleans it up (effect-cleanup discipline; the burst must not outlive the
  // screen when the user taps "Run demo again").
  useEffect(() => {
    const fire = (particleRatio: number, opts: confetti.Options): void => {
      void confetti({
        origin: { y: 0.6 },
        colors: CONFETTI_COLORS,
        particleCount: Math.floor(220 * particleRatio),
        ...opts,
      })
    }
    fire(0.25, { spread: 26, startVelocity: 55 })
    fire(0.2, { spread: 60 })
    fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 })
    fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 })
    fire(0.1, { spread: 120, startVelocity: 45 })
    return () => {
      confetti.reset()
    }
  }, [])

  return (
    <AppShell fullbleed>
      {/* installer-surface must be an ANCESTOR of .complete-overlay (rules are
          descendant-scoped). It fills the fullbleed (relative) main. */}
      <div className="installer-surface" style={{ position: 'absolute', inset: 0 }}>
        <div className="complete-overlay visible">
          <div className="complete-icon">
            <Icon.CheckBig />
          </div>
          <div className="complete-title">Job closed</div>
          <div className="complete-sub">
            Proof bundle sealed and submitted. Installer cleared to depart.
          </div>
          <button
            className="complete-restart"
            onClick={() => {
              dispatch({ type: 'RESET' })
              goto('start')
            }}
          >
            Run demo again
          </button>
        </div>
      </div>
    </AppShell>
  )
}
